import { useEffect, useMemo, useState } from "react";
import { createDefaultConfig, DEFAULT_MATCH_STEPS, durationTicksForSteps, AI_DECISION_INTERVAL_TICKS } from "@cc/engine";
import { ArenaCanvas } from "./ArenaCanvas";
import { BattleTimeline } from "./Timeline";
import { Hud } from "./Hud";
import { ResultOverlay } from "./ResultOverlay";
import { findCricket, isAiCricketId } from "./crickets";
import type { Locale, Dict } from "./i18n";
import { MatchClient } from "./match-client";
import { parseMatchId } from "./match-id";
import { navigate } from "./router";

/**
 * Replay a match from its id.
 *
 * The important property: `/replay/collector~hunter~421337` rebuilds the match
 * by **re-running the deterministic engine from the id alone**. Nothing was
 * stored, so nothing can be missing, stale, or migrated — the id is a recipe,
 * not a database key. This is what makes the replayability claim real rather
 * than decorative: anyone can verify a result from public data.
 *
 * One honest limitation, surfaced rather than hidden: a match involving the
 * session AI cricket cannot be fully reconstructed, because its decisions came
 * from a network model that will not answer identically twice. The engine can
 * replay the *recorded actions* exactly, but the page has no stored log — so for
 * an AI match this shows a fresh play-out and says so.
 */
export function Replay({
  id,
  locale,
  d,
}: {
  id: string;
  locale: Locale;
  d: Dict;
}) {
  const parsed = useMemo(() => parseMatchId(id), [id]);
  const [client, setClient] = useState<MatchClient | null>(null);
  const [speed, setSpeed] = useState(4);
  const [paused, setPaused] = useState(true);
  const [showResult, setShowResult] = useState(true);
  const [tickView, setTickView] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!parsed) {
      setError("bad-id");
      return;
    }
    try {
      const left = findCricket(parsed.agentIds[0]);
      const right = findCricket(parsed.agentIds[1]);
      const liveAi = isAiCricketId(left.id) || isAiCricketId(right.id);
      const interval = liveAi ? AI_DECISION_INTERVAL_TICKS : undefined;
      const config = createDefaultConfig({
        seed: parsed.seed,
        durationTicks: durationTicksForSteps(DEFAULT_MATCH_STEPS, interval),
        agentIds: [left.id, right.id],
        ...(liveAi
          ? { decisionIntervalTicks: AI_DECISION_INTERVAL_TICKS, alignedDecisions: true }
          : {}),
      });
      const next = new MatchClient(
        config,
        [left.create(), right.create()],
        [
          { slot: 0, id: left.id, persona: left.persona, model: left.model, icon: left.icon },
          { slot: 1, id: right.id, persona: right.persona, model: right.model, icon: right.icon },
        ],
      );
      next.setLocale(locale);
      // A replay should be watchable immediately: run it to completion so the
      // result and the full battle timeline are available, then let the viewer
      // scrub. Re-running a 90-step match at 10 Hz is a fraction of a second of
      // CPU, so there is no reason to make them wait through it in real time.
      next.fastForward();
      setClient(next);
      setTickView(next.tick);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "unknown");
    }
  }, [parsed, locale]);

  useEffect(() => {
    if (!client) return;
    client.setLocale(locale);
  }, [client, locale]);

  if (error === "bad-id") {
    return (
      <div className="page">
        <header className="page-head">
          <h1>{d.replayTitle}</h1>
        </header>
        <p className="err">{d.replayInvalid}</p>
        <button onClick={() => navigate({ name: "live" })}>{d.back}</button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <header className="page-head">
          <h1>{d.replayTitle}</h1>
        </header>
        <p className="err">{error}</p>
        <button onClick={() => navigate({ name: "live" })}>{d.back}</button>
      </div>
    );
  }

  const outcome = client?.getResult() ?? null;

  return (
    <div className="replay">
      <div className="topbar">
        <div className="brand">
          <h1>{d.replayTitle}</h1>
          <span className="tagline mono">{id}</span>
        </div>
        <div className="controls">
          <button className={paused ? "active" : ""} onClick={() => setPaused((p) => !p)}>
            {paused ? d.resume : d.pause}
          </button>
          {([1, 2, 4] as const).map((s) => (
            <button key={s} className={speed === s ? "active" : ""} onClick={() => setSpeed(s)}>
              {s}×
            </button>
          ))}
          <button className="ghost" onClick={() => navigate({ name: "live" })}>
            {d.navLive}
          </button>
        </div>
      </div>

      <div className="main">
        <div className="stage">
          {client ? <Hud client={client} locale={locale} d={d} /> : null}
          {client ? <ArenaCanvas client={client} speed={speed} paused={paused} d={d} /> : null}
          {client ? (
            <div className="scrubber">
              <span className="mono" style={{ fontSize: 10, color: "var(--dim)", minWidth: 34 }}>
                {Math.floor(client.tick / 10)}s
              </span>
              <input
                type="range"
                min={0}
                max={Math.max(1, client.tick)}
                value={Math.max(0, Math.min(tickView, client.tick))}
                onChange={(e) => {
                  const target = Number(e.target.value);
                  setPaused(true);
                  client.seekTo(target);
                  setTickView(target);
                }}
              />
              <span
                className="mono"
                style={{ fontSize: 10, color: "var(--dim)", minWidth: 34, textAlign: "right" }}
              >
                {Math.floor(client.config.durationTicks / 10)}
              </span>
            </div>
          ) : null}
        </div>

        <div className="side">
          {client ? <BattleTimeline client={client} locale={locale} d={d} /> : null}
        </div>
      </div>

      {client && outcome && showResult ? (
        <ResultOverlay
          outcome={outcome}
          client={client}
          locale={locale}
          d={d}
          onRematch={() => {
            setShowResult(false);
            if (parsed) {
              const params = new URLSearchParams({
                a: parsed.agentIds[0]!,
                b: parsed.agentIds[1]!,
                seed: String(Math.floor(Math.random() * 1_000_000)),
              });
              window.history.pushState(null, "", `/?${params.toString()}`);
              window.dispatchEvent(new PopStateEvent("popstate"));
            } else {
              navigate({ name: "live" });
            }
          }}
          onNewDuel={() => {
            setShowResult(false);
            window.history.pushState(null, "", "/?duel=1");
            window.dispatchEvent(new PopStateEvent("popstate"));
          }}
          onViewDetails={() => setShowResult(false)}
        />
      ) : null}
    </div>
  );
}
